import { useState } from "react";
import { Upload, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

/**
 * Reusable file-upload field that POSTs to `endpoint` (e.g. /uploads/submission-photo)
 * and reports back the served public URL via `onUploaded(url)`.
 */
export default function FileUploadField({
    endpoint,
    accept = "image/*",
    label = "Upload file",
    testId = "file-upload",
    auth = false,
    onUploaded,
}) {
    const [name, setName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [doneUrl, setDoneUrl] = useState("");

    const handle = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setName(file.name);
        setUploading(true);
        try {
            const form = new FormData();
            form.append("file", file);
            const r = await api.post(endpoint, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = r.data.url; // relative /api/files/...
            const absolute = url.startsWith("http") ? url : `${API.replace(/\/api$/, "")}${url}`;
            setDoneUrl(absolute);
            onUploaded?.(absolute);
            toast.success("File uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
            setName("");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="flex flex-col gap-2" data-testid={`${testId}-wrapper`}>
            <span className="overline text-white">{label}</span>
            <label
                className={`border border-streetz-line bg-black px-4 py-3 flex items-center gap-3 cursor-pointer hover:border-streetz-red transition-colors ${uploading ? "opacity-60" : ""}`}
                data-testid={`${testId}-label`}
            >
                {uploading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-streetz-yellow" />
                ) : doneUrl ? (
                    <CheckCircle2 className="w-4 h-4 text-streetz-yellow" />
                ) : (
                    <Upload className="w-4 h-4 text-streetz-muted" />
                )}
                <span className="text-sm text-white/85 truncate flex-1">
                    {uploading ? "Uploading…" : name || "Choose file"}
                </span>
                <input
                    type="file"
                    accept={accept}
                    onChange={handle}
                    disabled={uploading}
                    className="hidden"
                    data-testid={`${testId}-input`}
                />
            </label>
            {doneUrl && (
                <a
                    href={doneUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="overline text-streetz-yellow hover:underline text-[10px]"
                    data-testid={`${testId}-preview`}
                >
                    PREVIEW ↗
                </a>
            )}
        </div>
    );
}
